import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { generateId } from '@/lib/app-utils';
import { logAction, createNotification } from '@/lib/audit';
import { 
  Check, X, Clock, Calendar as CalendarIcon, ChevronLeft, ChevronRight, 
  AlertCircle, PlusCircle, Filter, Info
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { overtimeAPI } from '@/services/overtime';

const OvertimePage = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [newRequest, setNewRequest] = useState({ date: '', hours: '', reason: '' });
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDayDetails, setSelectedDayDetails] = useState(null);
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    loadRequests();
  }, [user.id]); // Reload if user changes (security)

  const loadRequests = async () => {
  try {
    const res = await overtimeAPI.list(user.id);
    if (res.success) {
      setRequests(res.requests || []);
    } else {
      console.error(res.error);
    }
  } catch (e) {
    console.error('Failed to load overtime requests', e);
  }
};


  // --- Date Restriction Helpers ---
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const maxDateStr = today.toISOString().split('T')[0];
  
  const minDate = new Date(today);
  minDate.setDate(today.getDate() - 2);
  const minDateStr = minDate.toISOString().split('T')[0];

  const allowedDates = [];
  for (let i = 0; i < 3; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    allowedDates.push(d.toLocaleDateString('default', { month: 'short', day: 'numeric' }));
  }

  // --- Statistics Calculation ---
  const stats = useMemo(() => {
    return requests.reduce((acc, curr) => {
      const hours = parseFloat(curr.hours) || 0;
      if (curr.status === 'Approved') acc.approved += hours;
      else if (curr.status === 'Pending') acc.pending += hours;
      else if (curr.status === 'Rejected') acc.rejected += hours;
      return acc;
    }, { approved: 0, pending: 0, rejected: 0 });
  }, [requests]);

  // --- Calendar Logic ---
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0 = Sunday
    
    const days = [];
    
    // Add empty slots for previous month days
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    // Add actual days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const calendarDays = useMemo(() => getDaysInMonth(currentDate), [currentDate]);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const getRequestsForDate = (date) => {
  if (!date) return [];
  
  // ✅ تأكد من format YYYY-MM-DD بدون timezone issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;
  
  console.log('🔍 Searching for date:', dateStr);
  console.log('📊 All requests:', requests);
  
  const filtered = requests.filter(r => {
    console.log('Comparing:', r.date, '===', dateStr, '→', r.date === dateStr);
    return r.date === dateStr;
  });
  
  console.log('✅ Filtered results:', filtered);
  return filtered;
};


  const handleDayClick = (date, dayRequests) => {
    if (!date || dayRequests.length === 0) return;
    setSelectedDayDetails({ date, requests: dayRequests });
  };

  const validateDate = (dateString) => {
    if (!dateString) return "Date is required";
    
    const selected = new Date(dateString);
    selected.setHours(0,0,0,0);
    
    const diffTime = today.getTime() - selected.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return "Cannot request overtime for future dates.";
    if (diffDays > 2) return "You can only request overtime for the last 2 days.";
    
    return "";
  };

  const handleDateChange = (e) => {
    const val = e.target.value;
    const error = validateDate(val);
    setDateError(error);
    setNewRequest({...newRequest, date: val});
  };

  // --- Form Handling ---
  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!newRequest.date || !newRequest.hours || !newRequest.reason) return;

  const error = validateDate(newRequest.date);
  if (error) {
    setDateError(error);
    return;
  }

  try {
    const payload = {
      userId: user.id,
      userName: user.name,
      department: user.department,
      date: newRequest.date,
      hours: parseFloat(newRequest.hours),
      reason: newRequest.reason,
    };

    const res = await overtimeAPI.create(payload);

    if (!res.success) {
      throw new Error(res.error || 'Failed to create request');
    }

    const request = res.request;

    setRequests(prev => [request, ...prev]);
    setNewRequest({ date: '', hours: '', reason: '' });
    setIsRequestDialogOpen(false);
    setDateError('');

    toast({
      title: 'Overtime Requested',
      description: 'Your request has been submitted for approval.',
      className: 'bg-green-50 text-green-800 border-green-200',
    });

    logAction(user, 'CREATE_OVERTIME', `Requested ${request.hours} hours for ${request.date}`);
    createNotification('ADMIN', 'INFO', `${user.name} requested overtime.`);
  } catch (err) {
    console.error(err);
    toast({
      title: 'Error',
      description: err.message || 'Failed to submit overtime request',
      variant: 'destructive',
    });
  }
};


  const openNewRequest = () => {
    setNewRequest({ date: '', hours: '', reason: '' });
    setDateError('');
    setIsRequestDialogOpen(true);
  };

  // Helper for status colors
  const getStatusColor = (status) => {
    switch(status) {
      case 'Approved': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Pending': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Rejected': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getStatusDotColor = (status) => {
    switch(status) {
      case 'Approved': return 'bg-emerald-500';
      case 'Pending': return 'bg-amber-500';
      case 'Rejected': return 'bg-red-500';
      default: return 'bg-slate-500';
    }
  };

  return (
    <TooltipProvider>
      <Helmet><title>My Overtime - Stone Beam</title></Helmet>
      <div className="space-y-8 max-w-7xl mx-auto pb-12">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-navy-steal">My Overtime</h1>
            <p className="text-stone-500 mt-1">Track your extra hours and approval status</p>
          </div>
          <Button onClick={openNewRequest} className="bg-safety-orange hover:bg-orange-600 shadow-md">
            <PlusCircle className="w-4 h-4 mr-2" /> Request Overtime
          </Button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
           {/* Approved */}
           <Card className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-shadow">
             <CardContent className="p-6">
               <div className="flex justify-between items-start">
                 <div>
                   <p className="text-sm font-medium text-stone-500 uppercase tracking-wider">Approved Hours</p>
                   <h3 className="text-3xl font-bold text-emerald-700 mt-2">{stats.approved}</h3>
                 </div>
                 <div className="p-3 bg-emerald-50 rounded-full">
                   <Check className="w-6 h-6 text-emerald-600" />
                 </div>
               </div>
             </CardContent>
           </Card>

           {/* Pending */}
           <Card className="border-l-4 border-l-amber-500 shadow-sm hover:shadow-md transition-shadow">
             <CardContent className="p-6">
               <div className="flex justify-between items-start">
                 <div>
                   <p className="text-sm font-medium text-stone-500 uppercase tracking-wider">Pending Approval</p>
                   <h3 className="text-3xl font-bold text-amber-600 mt-2">{stats.pending}</h3>
                 </div>
                 <div className="p-3 bg-amber-50 rounded-full">
                   <Clock className="w-6 h-6 text-amber-600" />
                 </div>
               </div>
             </CardContent>
           </Card>

           {/* Rejected */}
           <Card className="border-l-4 border-l-red-500 shadow-sm hover:shadow-md transition-shadow">
             <CardContent className="p-6">
               <div className="flex justify-between items-start">
                 <div>
                   <p className="text-sm font-medium text-stone-500 uppercase tracking-wider">Rejected / Not Approved</p>
                   <h3 className="text-3xl font-bold text-red-600 mt-2">{stats.rejected}</h3>
                 </div>
                 <div className="p-3 bg-red-50 rounded-full">
                   <X className="w-6 h-6 text-red-600" />
                 </div>
               </div>
             </CardContent>
           </Card>
        </div>

        {/* Calendar Section */}
        <Card className="shadow-md border-stone-200">
          <CardHeader className="border-b border-stone-100 bg-white rounded-t-xl pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-bold text-navy-steal flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-safety-orange" />
                  {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                </h2>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" onClick={handlePrevMonth} className="h-9 w-9">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => setCurrentDate(new Date())} className="h-9 text-xs font-normal">
                  Today
                </Button>
                <Button variant="outline" size="icon" onClick={handleNextMonth} className="h-9 w-9">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Days Header */}
            <div className="grid grid-cols-7 border-b border-stone-100 bg-slate-50/50">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="py-3 text-center text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 auto-rows-[minmax(100px,1fr)] bg-stone-100 gap-px border-b border-stone-200">
              {calendarDays.map((date, index) => {
                const dayRequests = getRequestsForDate(date);
                const hasRequests = dayRequests.length > 0;
                const isToday = date && date.toDateString() === new Date().toDateString();
                const isWeekend = date && (date.getDay() === 0 || date.getDay() === 6);

                return (
                  <div 
                    key={index} 
                    className={cn(
                      "bg-white relative p-2 min-h-[120px] transition-colors",
                      !date && "bg-slate-50", // Empty cells
                      date && hasRequests && "hover:bg-slate-50 cursor-pointer",
                      date && isWeekend && "bg-slate-50/30"
                    )}
                    onClick={() => handleDayClick(date, dayRequests)}
                  >
                    {date && (
                      <>
                        <div className={cn(
                          "flex justify-between items-start mb-2",
                        )}>
                          <span className={cn(
                            "text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full",
                            isToday ? "bg-navy-steal text-white shadow-sm" : "text-stone-700"
                          )}>
                            {date.getDate()}
                          </span>
                          {hasRequests && (
                            <span className="text-[10px] font-bold text-stone-400">
                              {dayRequests.length} Entry
                            </span>
                          )}
                        </div>

                        <div className="space-y-1">
                          {dayRequests.map((req, i) => (
                            <Tooltip key={req.id}>
                              <TooltipTrigger asChild>
                                <div className={cn(
                                  "text-xs px-2 py-1.5 rounded-md border flex items-center justify-between gap-1 shadow-sm transition-all hover:scale-[1.02]",
                                  getStatusColor(req.status)
                                )}>
                                  <span className="font-bold">{req.hours}h</span>
                                  <div className={cn("w-2 h-2 rounded-full shrink-0", getStatusDotColor(req.status))} />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="font-semibold">{req.reason}</p>
                                <p className="text-xs opacity-90">{req.status}</p>
                              </TooltipContent>
                            </Tooltip>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="p-4 bg-slate-50 flex items-center gap-6 text-xs text-stone-600 border-t border-stone-100">
              <span className="font-semibold text-stone-400">Legend:</span>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" /> Approved
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" /> Pending
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" /> Rejected
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* New Request Modal */}
      <Dialog open={isRequestDialogOpen} onOpenChange={setIsRequestDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Submit Overtime Request</DialogTitle>
            <DialogDescription>
              Enter the date and hours you worked overtime.
            </DialogDescription>
          </DialogHeader>
          
          {/* User Guidance / Allowed Range Notice */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm flex gap-3 text-blue-800">
             <Info className="w-5 h-5 shrink-0 text-blue-600 mt-0.5" />
             <div className="space-y-1">
               <p className="font-medium">Allowed Dates:</p>
               <p className="text-xs opacity-90">
                 You can only request overtime for today or the previous 2 days:
                 <br/>
                 <span className="font-semibold mt-1 inline-block">
                    {allowedDates.join(', ')}
                 </span>
               </p>
             </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            <div className="grid gap-2">
              <label className="text-sm font-medium">Date</label>
              <Input 
                type="date" 
                required 
                value={newRequest.date} 
                min={minDateStr}
                max={maxDateStr}
                onChange={handleDateChange}
                className={dateError ? "border-red-500 bg-red-50" : ""}
              />
              {dateError && (
                 <div className="flex items-center gap-2 text-xs text-red-600 font-medium animate-in slide-in-from-top-1">
                    <AlertCircle className="w-3 h-3" /> {dateError}
                 </div>
              )}
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium">Total Hours</label>
              <Input 
                type="number" 
                step="0.5" 
                min="0.5" 
                max="24"
                required 
                placeholder="e.g. 2.5"
                value={newRequest.hours} 
                onChange={e => setNewRequest({...newRequest, hours: e.target.value})} 
              />
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium">Reason / Description</label>
              <Input 
                required 
                placeholder="Project urgency, Deadline, etc."
                value={newRequest.reason} 
                onChange={e => setNewRequest({...newRequest, reason: e.target.value})} 
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsRequestDialogOpen(false)}>Cancel</Button>
              <Button 
                type="submit" 
                disabled={!!dateError || !newRequest.date}
                className="bg-safety-orange hover:bg-orange-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Request
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Day Details Modal */}
      <Dialog open={!!selectedDayDetails} onOpenChange={(open) => !open && setSelectedDayDetails(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-navy-steal" />
              Overtime Details
            </DialogTitle>
            <DialogDescription>
              {selectedDayDetails?.date?.toDateString()}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {selectedDayDetails?.requests.map((req, idx) => (
              <div key={idx} className="bg-slate-50 p-4 rounded-lg border border-stone-200 space-y-3">
                 <div className="flex justify-between items-start">
                    <Badge variant="outline" className={cn("border-0", getStatusColor(req.status))}>
                      {req.status}
                    </Badge>
                    <span className="font-bold text-lg text-navy-steal">{req.hours} Hours</span>
                 </div>
                 <div>
                   <p className="text-xs text-stone-500 uppercase font-semibold">Reason</p>
                   <p className="text-sm text-stone-800">{req.reason}</p>
                 </div>
                 {req.actionBy && (
                   <div className="pt-2 border-t border-stone-200 mt-2">
                     <p className="text-xs text-stone-400">
                       {req.status} by <span className="font-medium text-stone-600">{req.actionBy}</span> on {new Date(req.actionDate).toLocaleDateString()}
                     </p>
                   </div>
                 )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setSelectedDayDetails(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default OvertimePage;